import {
  AddOn_Create,
  AddOn_Update,
  AddOnCharge_Create,
  AddOnCharge_Read,
  AddOnFuelSurchargeRate_Read,
  AddOnRateType,
  deleteV1CompaniesCompanyIdAddOnsId,
  getV1CompaniesCompanyIdAddOns,
  getV1CompaniesCompanyIdAddOnsId,
  getV1CompaniesCompanyIdAddOnsIdFuelSurchargeRate,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdAddOnCharges,
  getV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges,
  getV1CompaniesCompanyIdJobsJobIdAddOnCharges,
  getV1CompaniesCompanyIdSettlementsId,
  patchV1CompaniesCompanyIdAddOnsId,
  patchV1CompaniesCompanyIdDriverDaysDriverDayIdAddOnCharges,
  patchV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges,
  patchV1CompaniesCompanyIdJobsJobIdAddOnCharges,
  patchV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges,
  postV1CompaniesCompanyIdAddOns,
  postV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { Settlement } from '~hooks/useSettlements/models';
import { AddOnChargeDTO } from '~pages/Approvals/DriverPay/helpers';
import { AddOnDTO } from '~pages/Settings/RatesManagement/AddOnCharges/components/addOnFormSchema';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { AddOn, AddOnCharge } from './models';

type CommonSettlementAddOn = {
  addOnId: string;
  name: string;
};

type PercentageSettlementAddOn = {
  revenueCostRateType: AddOnRateType.RATE_PERCENT_OF_TOTAL;
  percentage: number;
} & CommonSettlementAddOn;

type RateSettlementAddOn = {
  revenueCostRateType: AddOnRateType.RATE_FOR_EACH;
  rate: number;
  quantity: number;
} & CommonSettlementAddOn;

type PerLoadSettlementAddOn = {
  revenueCostRateType: AddOnRateType.PER_LOAD;
  rate: number;
  quantity: number;
} & CommonSettlementAddOn;

type SettlementAddOn =
  | PercentageSettlementAddOn
  | RateSettlementAddOn
  | PerLoadSettlementAddOn;

interface GetAddOnsPageParams {
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  signal?: AbortSignal;
}

const useAddOns = () => {
  const { addOnsStore, settlementsStore } = useStores();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const createSettlementAddOnCharges = async (
    companyId: string,
    settlementId: string,
    addOns: SettlementAddOn[],
  ) => {
    try {
      setIsCreating(true);
      const updates = [];

      for (const addOn of addOns) {
        const payload: AddOnCharge_Create =
          addOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
            ? {
                add_on_id: addOn.addOnId,
                revenue_cost_rate_type: addOn.revenueCostRateType,
                name: addOn.name,
                percentage: addOn.percentage,
              }
            : {
                add_on_id: addOn.addOnId,
                revenue_cost_rate_type: addOn.revenueCostRateType,
                name: addOn.name,
                quantity: addOn.quantity,
                revenue_rate_value: addOn.rate,
              };

        const response =
          await postV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges({
            path: { 'company-id': companyId, 'settlement-id': settlementId },
            body: payload,
          });

        updates.push(response.data.data);
      }
      const responses = await Promise.all(updates);
      responses.forEach((charge) => {
        const addOnCharge = AddOnCharge.parse(charge);
        settlementsStore.updateAddOnChargeBySettlementId(settlementId, addOnCharge);
      });
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_create'));
    } finally {
      setIsCreating(false);
    }
  };

  const batchUpdateAddOnCharges = async (
    companyId: string,
    invoiceId: string,
    addOns: AddOnChargeDTO[],
  ) => {
    try {
      setIsUpdating(true);

      const addOnCharges = addOns
        .map((addOn) => AddOnCharge.deparse(addOn))
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const response = await patchV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges({
        path: { 'company-id': companyId, 'invoice-id': invoiceId },
        body: { add_on_charges: addOnCharges },
      });

      const updatedAddOnCharges = response.data.data.map(
        (addOnCharge: AddOnCharge_Read) => {
          return AddOnCharge.parse(addOnCharge);
        },
      );
      addOnsStore.setAddOnChargesByInvoiceId(invoiceId, updatedAddOnCharges);

      return updatedAddOnCharges;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_update'));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const createAddOnByCompanyId = async (companyId: string, data: AddOnDTO) => {
    try {
      setIsCreating(true);

      const response = await postV1CompaniesCompanyIdAddOns({
        path: { 'company-id': companyId },
        body: AddOn.deparse(data) as AddOn_Create,
      });

      const newAddOn = AddOn.parse(response.data.data);
      addOnsStore.addAddOn(newAddOn);

      return newAddOn;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_create'));
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const getAllAddOns = async (companyId: string) => {
    setIsLoading(true);

    try {
      const addOns = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdAddOns({ path: { 'company-id': companyId }, query }),
        AddOn.parse,
      );
      addOnsStore.setAddOns(addOns);

      return addOns;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_fetch'));
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAddOns = useCallback(
    async (companyId: string, { cursor, limit, link, signal }: GetAddOnsPageParams) => {
      const response = await getV1CompaniesCompanyIdAddOns({
        path: { 'company-id': companyId },
        query: {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map((addOn) => AddOn.parse(addOn)),
        pagination: extractPagination(response),
      };
    },
    [],
  );

  const getAllInvoiceAddOnCharges = async (companyId: string, invoiceId: string) => {
    setIsLoading(true);

    try {
      const addOnCharges = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges({
            path: { 'company-id': companyId, 'invoice-id': invoiceId },
            query,
          }),
        AddOnCharge.parse,
      );
      addOnsStore.setAddOnChargesByInvoiceId(invoiceId, addOnCharges);

      return addOnCharges;
    } finally {
      setIsLoading(false);
    }
  };

  const updateAddOn = async (companyId: string, addOnId: string, data: AddOnDTO) => {
    try {
      setIsUpdating(true);

      const response = await patchV1CompaniesCompanyIdAddOnsId({
        path: { 'company-id': companyId, id: addOnId },
        body: AddOn.deparseUpdate(data) as AddOn_Update,
      });

      const updatedAddOn = AddOn.parse(response.data.data);
      addOnsStore.updateAddOn(updatedAddOn);

      return updatedAddOn;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_update'));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const batchUpdateAddOnChargesOfSettlements = async (
    companyId: string,
    settlementId: string,
    addOns: AddOnChargeDTO[],
  ) => {
    try {
      setIsUpdating(true);

      const addOnCharges = addOns
        .map((addOn) => AddOnCharge.deparse(addOn, true))
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const response = await patchV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges(
        {
          path: { 'company-id': companyId, 'settlement-id': settlementId },
          body: { add_on_charges: addOnCharges },
        },
      );
      const updatedAddOnCharges = response.data.data.map((addOnCharge) => {
        return AddOnCharge.parse(addOnCharge);
      });
      settlementsStore.setAddOnChargesBySettlementId(settlementId, updatedAddOnCharges);

      let refreshedSettlement: Settlement | null = null;
      try {
        const settlementResponse = await getV1CompaniesCompanyIdSettlementsId({
          path: { 'company-id': companyId, id: settlementId },
        });
        refreshedSettlement = Settlement.parse(settlementResponse.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.settlements.failed_to_fetch'),
          );
        } catch {
          // The add-ons are already saved; a failed totals refresh must not fail the save.
        }
      }

      return { addOnCharges: updatedAddOnCharges, settlement: refreshedSettlement };
    } catch (error) {
      return connection.handleRequestError(
        error,
        t('error_messages.add_ons.failed_to_update'),
      );
    } finally {
      setIsUpdating(false);
    }
  };
  const getAllAddOnChargesByDriverDay = async (
    companyId: string,
    driverDayId: string,
  ) => {
    setIsLoading(true);

    try {
      const addOnCharges = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdDriverDaysDriverDayIdAddOnCharges({
            path: { 'company-id': companyId, 'driver-day-id': driverDayId },
            query,
          }),
        AddOnCharge.parse,
      );
      addOnsStore.setAddOnChargesByDriverDayId(driverDayId, addOnCharges);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.add_ons.failed_to_fetch_charges'),
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const batchUpdateAddOnChargesByDriverDay = async (
    companyId: string,
    driverDayId: string,
    addOns: AddOnChargeDTO[],
  ) => {
    setIsUpdating(true);
    try {
      const addOnCharges = addOns
        .map((addOn) => AddOnCharge.deparse(addOn))
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const response = await patchV1CompaniesCompanyIdDriverDaysDriverDayIdAddOnCharges({
        path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        body: { add_on_charges: addOnCharges },
      });

      const updatedAddOnCharges = response.data.data.map((addOnCharge) => {
        return AddOnCharge.parse(addOnCharge);
      });
      addOnsStore.setAddOnChargesByDriverDayId(driverDayId, updatedAddOnCharges);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_update'));
    } finally {
      setIsUpdating(false);
    }
  };

  const batchUpdateAddOnChargesByJob = async (
    companyId: string,
    jobId: string,
    addOns: AddOnChargeDTO[],
  ) => {
    setIsUpdating(true);
    try {
      const addOnCharges = addOns
        .map((addOn) => AddOnCharge.deparse(addOn))
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const response = await patchV1CompaniesCompanyIdJobsJobIdAddOnCharges({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: { add_on_charges: addOnCharges },
      });

      const updatedAddOnCharges = response.data.data.map((addOnCharge) => {
        return AddOnCharge.parse(addOnCharge);
      });

      addOnsStore.setAddOnChargesByJobId(jobId, updatedAddOnCharges);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_update'));
    } finally {
      setIsUpdating(false);
    }
  };

  const getAllAddOnChargesByJob = async (companyId: string, jobId: string) => {
    setIsLoading(true);

    try {
      const addOnCharges = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdJobsJobIdAddOnCharges({
            path: { 'company-id': companyId, 'job-id': jobId },
            query,
          }),
        AddOnCharge.parse,
      );

      addOnsStore.setAddOnChargesByJobId(jobId, addOnCharges);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.add_ons.failed_to_fetch_charges'),
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAddOn = async (companyId: string, addOnId: string) => {
    try {
      const response = await getV1CompaniesCompanyIdAddOnsId({
        path: { 'company-id': companyId, id: addOnId },
      });

      return AddOn.parse(response.data.data);
    } catch (error) {
      console.error(error);
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_fetch'));
    }
  };

  const getFuelSurchargeRate = useCallback(
    async (
      companyId: string,
      addOnId: string,
      date?: string,
      signal?: AbortSignal,
    ): Promise<AddOnFuelSurchargeRate_Read | null> => {
      try {
        const response = await getV1CompaniesCompanyIdAddOnsIdFuelSurchargeRate({
          path: { 'company-id': companyId, id: addOnId },
          query: date ? { 'filter[date]': date } : undefined,
          signal,
        });
        return response.data.data;
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }
        // Ignore 404 errors - no rate configured for this date is a valid scenario
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        connection.handleRequestError(
          error,
          t('error_messages.add_ons.failed_to_fetch_fuel_surcharge_rate'),
        );

        return null;
      }
    },
    [],
  );

  const deleteAddOn = useCallback(async (companyId: string, addOnId: string) => {
    try {
      await deleteV1CompaniesCompanyIdAddOnsId({
        path: { 'company-id': companyId, id: addOnId },
      });

      addOnsStore.deleteAddOn(addOnId);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.add_ons.failed_to_delete'));
      throw error;
    }
  }, []);

  return {
    createSettlementAddOnCharges,
    batchUpdateAddOnCharges,
    batchUpdateAddOnChargesByDriverDay,
    batchUpdateAddOnChargesByJob,
    batchUpdateAddOnChargesOfSettlements,
    createAddOnByCompanyId,
    deleteAddOn,
    getAddOns,
    getAllAddOnChargesByJob,
    getAllAddOns,
    getAllInvoiceAddOnCharges,
    getAllAddOnChargesByDriverDay,
    getFuelSurchargeRate,
    isCreating,
    isLoading,
    isUpdating,
    updateAddOn,
    getAddOn,
  };
};

export default useAddOns;
